import { analyticsContext, isAnalyticsEnabled } from "@shared/analytics/posthog";
import { eventId, type EventOwner, ownerForAddress } from "@shared/analytics/walletEvents";
import { walletJourney } from "@shared/analytics/walletJourney";
import type { ApplicationDraft } from "@shared/widgets/yaas/types";
import posthog from "posthog-js";

export type EnrollmentEvent =
    | "enroll:requested"
    | "enroll:form_viewed"
    | "enroll:form_closed"
    | "enroll:distribution_selected"
    | "enroll:destination_updated"
    | "enroll:validation_updated"
    | "enroll:check_started"
    | "enroll:check_completed"
    | "enroll:check_failed"
    | "enroll:retry_clicked"
    | "enroll:started"
    | "enroll:challenge_started"
    | "enroll:challenge_completed"
    | "enroll:challenge_failed"
    | "enroll:verification_method_selected"
    | "enroll:verification_consent_viewed"
    | "enroll:verification_consent_accepted"
    | "enroll:verification_consent_rejected"
    | "enroll:verification_started"
    | "enroll:proof_received"
    | "enroll:verification_failed"
    | "enroll:verification_resumed"
    | "enroll:submitted"
    | "enroll:completed"
    | "enroll:failed"
    | "enroll:confirmation_deferred"
    | "enroll:result_viewed"
    | "enroll:transfer_started"
    | "enroll:transfer_submitted"
    | "enroll:transfer_failed"
    | "enroll:transfer_confirmation_deferred"
    | "terms:modal_opened"
    | "terms:checkbox_toggled"
    | "terms:accepted"
    | "terms:rejected";

/** Only explicit product fields are permitted. Never pass SDK errors or form objects. */
export interface EnrollmentProperties {
    enrollment_flow_id?: string;
    enrollment_attempt_id?: string;
    verification_id?: string;
    transaction_attempt_id?: string;
    resumed_from_flow_id?: string;
    view_id?: string;
    attempt_number?: number;
    retry_of_attempt_id?: string;
    attempt_kind?: "new_verification" | "proof_recovery";
    origin?: "imported";
    entry_point?: "rewards_entry";
    check_source?: "cached" | "fresh";
    phase?: string;
    reason?: string;
    close_method?: string;
    dirty?: boolean;
    request_pending?: boolean;
    proof_pending?: boolean;
    has_pending_proof?: boolean;
    distribution_mode?: "stream" | "claim";
    previous_mode?: "stream" | "claim";
    destination_kind?: "self" | "custom";
    has_label?: boolean;
    address_valid?: boolean;
    can_submit?: boolean;
    blocking_reasons?: string[];
    check_id?: string;
    check_type?: "terms" | "destination";
    trigger?: string;
    result?: string;
    retry_target?: string;
    retry_action?: string;
    verification_method?: "sign_message" | "micro_transfer";
    method?: string;
    method_used?: string;
    method_attempted?: string;
    proof_source?: "fresh" | "journal" | "pending_operation";
    transfer_available?: boolean;
    fallback_triggered?: boolean;
    failure_stage?: string;
    step?: string;
    error_code?: string;
    http_status?: number | null;
    created?: boolean;
    account_status?: string;
    is_rewards_eligible?: boolean;
    duration_ms?: number;
    total_flow_ms?: number;
    verify_latency_ms?: number;
    surface?: "main_app" | "retail_enrollment";
    control?: "inline" | "modal";
    checked?: boolean;
    had_checked_box?: boolean;
    acceptance_latency_ms?: number;
    terms_document_key?: string;
    terms_version?: string;
    terms_content_digest?: string;
}
export type EnrollmentEmit = (event: EnrollmentEvent, properties: EnrollmentProperties, owner: EventOwner) => void;
export type VerificationObserver = (event: EnrollmentEvent, properties?: EnrollmentProperties) => void;

export function captureEnrollmentEvent(
    event: EnrollmentEvent,
    properties: EnrollmentProperties,
    owner: EventOwner
): void {
    if (!isAnalyticsEnabled()) return;
    const payload = {
        ...analyticsContext(),
        ...owner.properties,
        ...properties,
        flow: "enroll",
        purpose: "enroll",
        schema_version: 3,
        route: window.location.pathname
    };
    void owner.identity
        ?.then((identity) => {
            if (identity) posthog.capture(event, { ...payload, distinct_id: identity });
        })
        .catch(() => undefined);
}

export interface VerificationAnalyticsRecord {
    flowId: string;
    verificationId: string;
    transactionId?: string;
    proofObserved?: boolean;
    origin?: "imported";
}
export function readVerificationAnalytics(value: unknown): VerificationAnalyticsRecord | undefined {
    if (
        !value ||
        typeof value !== "object" ||
        !("flowId" in value) ||
        typeof value.flowId !== "string" ||
        !("verificationId" in value) ||
        typeof value.verificationId !== "string"
    )
        return undefined;
    return {
        flowId: value.flowId,
        verificationId: value.verificationId,
        transactionId:
            "transactionId" in value && typeof value.transactionId === "string" ? value.transactionId : undefined,
        proofObserved: "proofObserved" in value && value.proofObserved === true,
        origin: "origin" in value && value.origin === "imported" ? "imported" : undefined
    };
}

export class EnrollmentJourney {
    readonly id = eventId();
    readonly owner: EventOwner;
    readonly viewId = eventId();
    private viewed = false;
    private closed = false;
    private count = 0;
    private lastAttempt?: EnrollmentAttempt;
    private readonly started = performance.now();
    private formProperties: EnrollmentProperties = {};
    updateForm(properties: EnrollmentProperties): void {
        this.formProperties = properties;
    }
    private readonly views = new Map<string, string>();
    constructor(
        readonly address: string,
        private readonly enabled = true,
        private readonly emit: EnrollmentEmit = captureEnrollmentEvent
    ) {
        this.owner = ownerForAddress(address, walletJourney.owner().properties);
    }
    send(event: EnrollmentEvent, properties: EnrollmentProperties = {}): void {
        if (!this.enabled) return;
        try {
            this.emit(
                event,
                {
                    entry_point: "rewards_entry",
                    enrollment_flow_id: this.id,
                    view_id: this.viewId,
                    enrollment_attempt_id: this.lastAttempt?.id,
                    verification_id: this.lastAttempt?.record.verificationId,
                    ...properties
                },
                this.owner
            );
        } catch {
            /* Telemetry cannot block enrollment. */
        }
    }
    view(properties: EnrollmentProperties): void {
        if (this.viewed) return;
        this.viewed = true;
        this.send("enroll:form_viewed", properties);
    }
    changed(event: EnrollmentEvent, properties: EnrollmentProperties): void {
        const key = JSON.stringify(properties);
        if (this.views.get(event) === key) return;
        this.views.set(event, key);
        this.send(event, properties);
    }
    close(reason: string, properties: EnrollmentProperties = {}): void {
        if (this.closed) return;
        this.closed = true;
        this.send("enroll:form_closed", {
            phase: this.lastAttempt?.phase ?? "idle",
            request_pending: this.lastAttempt?.pending ?? false,
            proof_pending: this.lastAttempt?.proofPending ?? false,
            ...this.formProperties,
            ...properties,
            close_method: reason,
            duration_ms: performance.now() - this.started
        });
    }
    attempt(draft: ApplicationDraft, restored?: VerificationAnalyticsRecord): EnrollmentAttempt {
        const attempt = new EnrollmentAttempt(this, ++this.count, draft, restored, this.lastAttempt?.id);
        this.lastAttempt = attempt;
        return attempt;
    }
}

export class EnrollmentAttempt {
    readonly id = eventId();
    readonly record: VerificationAnalyticsRecord;
    phase = "challenge";
    pending = true;
    proofPending = false;
    private terminal = false;
    private deferred = false;
    private transferSubmitted = false;
    private transferDeferred = false;
    private transferFailed = false;
    private readonly started = performance.now();
    private readonly properties: EnrollmentProperties;
    constructor(
        private readonly journey: EnrollmentJourney,
        number: number,
        draft: ApplicationDraft,
        restored?: VerificationAnalyticsRecord,
        retryOf?: string
    ) {
        this.record = restored ?? { flowId: journey.id, verificationId: eventId() };
        this.properties = {
            enrollment_attempt_id: this.id,
            verification_id: this.record.verificationId,
            attempt_number: number,
            retry_of_attempt_id: retryOf,
            origin: this.record.origin,
            resumed_from_flow_id: this.record.flowId !== journey.id ? this.record.flowId : undefined,
            distribution_mode: draft.distributionMode,
            destination_kind:
                !draft.rewardsDestination || draft.rewardsDestination === journey.address ? "self" : "custom",
            has_label: !!draft.accountLabel.trim(),
            terms_version: draft.terms?.version,
            proof_source: restored ? "journal" : "fresh",
            attempt_kind: restored ? "proof_recovery" : "new_verification"
        };
        this.send("enroll:started");
    }
    send(event: EnrollmentEvent, properties: EnrollmentProperties = {}): void {
        this.journey.send(event, {
            ...this.properties,
            transaction_attempt_id: this.record.transactionId,
            ...properties
        });
    }
    observe: VerificationObserver = (event, properties = {}): void => {
        if (event === "enroll:verification_resumed" && properties.proof_source === "pending_operation")
            this.proofPending = true;
        if (event === "enroll:transfer_started") this.record.transactionId ??= eventId();
        if (event === "enroll:transfer_failed") {
            if (this.transferFailed) return;
            this.transferFailed = true;
            this.proofPending = false;
        }
        if (event === "enroll:transfer_submitted") {
            if (this.transferSubmitted) return;
            this.transferSubmitted = true;
            this.proofPending = true;
        }
        if (event === "enroll:transfer_confirmation_deferred") {
            if (this.transferDeferred) return;
            this.transferDeferred = true;
            this.proofPending = true;
        }
        if (event === "enroll:proof_received") {
            if (this.record.proofObserved) return;
            this.record.proofObserved = true;
        }
        this.send(event, properties);
    };
    submitted(method: "signMessage" | "microTransfer"): void {
        this.properties.verification_method = method === "microTransfer" ? "micro_transfer" : "sign_message";
        this.phase = "submit";
        this.send("enroll:submitted", {
            verification_method: method === "microTransfer" ? "micro_transfer" : "sign_message"
        });
    }
    succeeded(created: boolean, eligible: boolean): void {
        if (this.terminal) return;
        this.terminal = true;
        this.pending = false;
        this.proofPending = false;
        this.phase = "submitted";
        this.send("enroll:completed", {
            created,
            account_status: "active",
            is_rewards_eligible: eligible,
            total_flow_ms: performance.now() - this.started
        });
    }
    failed(code: string, status: number | null, uncertain = false): void {
        if (this.terminal || this.deferred) return;
        this.pending = false;
        this.deferred = uncertain;
        this.terminal = !uncertain;
        this.send(uncertain ? "enroll:confirmation_deferred" : "enroll:failed", {
            failure_stage: this.phase,
            step: this.phase,
            error_code: code,
            http_status: status,
            reason: uncertain ? code : undefined,
            total_flow_ms: performance.now() - this.started
        });
    }
}
